import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Franja } from '../componentes/Franja';
import { Ayuda } from '../componentes/Ayuda';
import { Contexto } from '../componentes/Contexto';
import { Boton } from '../componentes/Boton';
import { Icono } from '../componentes/Icono';
import { grupoAbiertoPorDefecto, grupoPlegable } from '../logica/navegacion';
import logo from '../estilo/comun/assets/wordmark-cropped.png';
import { dibujoDe } from './iconosMenu';
import type { Vista } from './PanelNegocio';
import { inicialesDe, lineaDe } from './negocio';
import { PersonalizarMenu } from './Personalizar';
import { Avatar, EtiquetaEstado, TarjetaAviso } from './piezas';
import { Privacidad } from './Privacidad';
import { useAnchoVentana, usePunteroFino } from './tamano';

/**
 * Marco de ordenador (≥ 1024; componentes.md §1, §1c, §1g): menú lateral negro de 248 con logo y campana, selector de
 * negocio, menú por categorías (G128: Operativa fija, el resto plegables con punto o cifra al plegarlas), «Más ›» con las
 * quitadas (G129) y la tarjeta de la persona; el negro sigue por arriba en una banda con la curva en S antes de las
 * herramientas (cabecera en curva); campana, negocios, tu cuenta, «Más» y «Personalizar menú» se abren ensanchando el
 * negro hacia la derecha (248 → 640, «cajón de sastre», §1g).
 *
 * Alto de las filas del menú (§1g, «Menú junto»): con ratón (`hover: hover` y `pointer: fine`) se reparten el alto
 * medido del menú, entre 26 y 40 (34 a 1440 × 900); en táctil (iPad), 44 y lo que no quepa con el scroll del menú.
 */
const CURVA = { hL: 28, hR: 100, sW: 96, aire: 16, r: 24, R: 28, x0: 16 + 248 - 28 };
const CLAVE_ABIERTAS = 'reservaria.menu-abiertas';

type Cajon = 'campana' | 'negocios' | 'cuenta' | 'mas' | 'personalizar' | null;

function leerAbiertas(): Record<string, boolean> {
    try {
        return JSON.parse(window.sessionStorage.getItem(CLAVE_ABIERTAS) ?? '{}') as Record<string, boolean>;
    } catch {
        return {};
    }
}

export function MarcoOrdenador({ v }: { v: Vista }) {
    const [cajon, setCajon] = useState<Cajon>(null);
    const [ultimo, setUltimo] = useState<Cajon>(null);
    const ancho = useAnchoVentana();
    const fino = usePunteroFino();
    const herramientas = useRef<HTMLDivElement>(null);
    const svg = useRef<SVGSVGElement>(null);
    const [curvaX, setCurvaX] = useState<number | null>(null);
    const nav = useRef<HTMLElement>(null);
    const [altoNav, setAltoNav] = useState(0);
    const [abiertas, setAbiertas] = useState<Record<string, boolean>>(leerAbiertas);

    const abrir = (c: Cajon) => {
        setCajon((actual) => (actual === c ? null : c));
        if (c !== null) {
            setUltimo(c);
        }
        if (c === 'campana') {
            v.campana.abrir();
        }
    };

    useEffect(() => {
        if (cajon === null) {
            return;
        }
        const esc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setCajon(null);
            }
        };
        window.addEventListener('keydown', esc);
        return () => {
            window.removeEventListener('keydown', esc);
        };
    }, [cajon]);

    // La curva en S acaba 16 px antes del grupo de herramientas y sigue su ancho real (ResizeObserver).
    useLayoutEffect(() => {
        const coloca = () => {
            const g = herramientas.current;
            const s = svg.current;
            if (g === null || s === null) {
                return;
            }
            setCurvaX(Math.round(g.getBoundingClientRect().left - s.getBoundingClientRect().left - CURVA.aire - CURVA.sW));
        };
        coloca();
        const ro = new ResizeObserver(coloca);
        if (herramientas.current !== null) {
            ro.observe(herramientas.current);
        }
        return () => {
            ro.disconnect();
        };
    }, [ancho, v.soloLectura]);

    useLayoutEffect(() => {
        const n = nav.current;
        if (n === null) {
            return;
        }
        const mide = () => {
            setAltoNav(n.clientHeight);
        };
        mide();
        const ro = new ResizeObserver(mide);
        ro.observe(n);
        return () => {
            ro.disconnect();
        };
    }, []);

    const { hL, hR, sW, r, R, x0 } = CURVA;
    const W = Math.max(400, ancho - 24 - x0);
    const A = 248 - x0 + 16;
    const k = Math.round(sW / 2);
    const x = curvaX ?? W - sW - 48;

    // Menú: grupos abiertos, alto de cada fila y posición de la píldora roja (0.1, §1g).
    const grupos = v.menu.grupos;
    const abierto = (clave: string) => !grupoPlegable(v.menu, clave) || (abiertas[clave] ?? grupoAbiertoPorDefecto(clave));
    const actualClave = v.actual?.clave ?? null;
    // Al entrar en una sección, su categoría se abre sola (§1g); lo demás plegado o abierto se queda como estaba.
    const [vistaAntes, setVistaAntes] = useState<string | null>(null);
    if (actualClave !== vistaAntes) {
        setVistaAntes(actualClave);
        const suya = grupos.find((g) => g.entradas.some((e) => e.clave === actualClave));
        if (suya !== undefined && grupoPlegable(v.menu, suya.clave) && !(abiertas[suya.clave] ?? grupoAbiertoPorDefecto(suya.clave))) {
            setAbiertas({ ...abiertas, [suya.clave]: true });
        }
    }
    const nAbiertas = grupos.reduce((t, g) => t + (abierto(g.clave) ? g.entradas.length : 0), 0);
    const nPlegadas = grupos.filter((g) => !abierto(g.clave)).length;
    const quitadas = v.menu.quitadas;
    const conMas = quitadas.length > 0;
    const mj = fino
        ? (() => {
              const H = 16;
              const G = 6;
              const fijo = grupos.length * H + nPlegadas * 14 + (grupos.length - 1) * G;
              // Con quitadas, la fila «Más ›» también cabe (una fila más y su separación).
              const fila = altoNav > 0 ? Math.floor((altoNav - fijo - (conMas ? G : 0)) / Math.max(1, nAbiertas + (conMas ? 1 : 0))) : 40;
              return { R: Math.max(26, Math.min(40, fila)), H, G, hPb: 2 };
          })()
        : { R: 44, H: 28, G: 16, hPb: 6 };
    let desplazamiento = 0;
    let pildora: number | null = null;
    for (const g of grupos) {
        const ab = abierto(g.clave);
        const alto = !ab && fino ? Math.max(mj.H, 30) : mj.H;
        const j = g.entradas.findIndex((e) => e.clave === actualClave && !e.noContratada);
        if (j >= 0 && ab) {
            pildora = desplazamiento + mj.H + j * mj.R;
        }
        desplazamiento += alto + (ab ? g.entradas.length * mj.R : 0) + mj.G;
    }

    const plegar = (clave: string) => {
        const nuevas = { ...abiertas, [clave]: !abierto(clave) };
        setAbiertas(nuevas);
        try {
            window.sessionStorage.setItem(CLAVE_ABIERTAS, JSON.stringify(nuevas));
        } catch {
            // Sin almacenamiento: se recuerda mientras la página esté abierta.
        }
    };

    const variosNegocios = v.membresias.length > 1;
    const enPerfil = v.actual !== null && v.menu.perfil.includes(v.actual);
    const enMas = quitadas.some((e) => e.clave === actualClave);
    const masAbierto = cajon === 'mas' || cajon === 'personalizar';
    const parte = (c: Exclude<Cajon, null>, desde: string) => {
        const on = cajon === c;
        return {
            'aria-hidden': !on,
            style: {
                visibility: on ? ('visible' as const) : ('hidden' as const),
                opacity: on ? 1 : 0,
                transform: on ? 'none' : desde,
                transitionDelay: on ? '120ms, 120ms, 0s' : `0ms, 0ms, 420ms`,
            },
        };
    };
    const entra = (on: boolean, i: number) => ({ opacity: on ? 1 : 0, transform: on ? 'none' : 'translateX(-16px)', transitionDelay: `${String(on ? 160 + i * 45 : 0)}ms` });
    const cnAbierto = cajon !== null;

    return (
        <div className="rv-marco">
            <svg ref={svg} className="rv-curva" aria-hidden="true" width={W} height={hR} viewBox={`0 0 ${String(W)} ${String(hR)}`} style={{ left: x0 }}>
                <defs>
                    <clipPath id="rv-curva-recorte">
                        <path d={`M0 0H${String(W - R)}A${String(R)} ${String(R)} 0 0 1 ${String(W)} ${String(R)}V${String(hR - R)}A${String(R)} ${String(R)} 0 0 1 ${String(W - R)} ${String(hR)}H0Z`} />
                    </clipPath>
                </defs>
                <g clipPath="url(#rv-curva-recorte)" fill="var(--rv-black)">
                    <path d={`M0 0H${String(W)}V${String(hL)}H${String(A + r)}A${String(r)} ${String(r)} 0 0 0 ${String(A)} ${String(hL + r)}H0Z`} />
                    <g style={{ transform: `translateX(${String(x)}px)`, transition: curvaX === null ? 'none' : 'transform var(--rv-dur-slide) var(--rv-ease-out)' }}>
                        <path d={`M0 0H${String(W + 400)}V${String(hR)}H${String(sW)}C${String(sW - k)} ${String(hR)} ${String(k)} ${String(hL)} 0 ${String(hL)}Z`} />
                    </g>
                </g>
            </svg>

            {/* Cajón: el negro del menú se ensancha hacia la derecha (campana, negocios, tu cuenta). */}
            <div className={`rv-cajon${cnAbierto ? ' rv-cajon--abierto' : ''}`}>
                <svg className="rv-cajon__esquina" aria-hidden="true" width={r + 1} height={r + 1} viewBox={`0 0 ${String(r + 1)} ${String(r + 1)}`} style={{ top: hL - 1 }}>
                    <path d={`M0 0H${String(r + 1)}V1A${String(r)} ${String(r)} 0 0 0 1 ${String(r + 1)}H0Z`} fill="var(--rv-black)" />
                </svg>
                <div className="rv-cajon__fondo">
                    <div role="dialog" aria-label="Avisos" className="rv-cajon__parte" {...parte('campana', 'translateX(-24px)')}>
                        <div className="rv-cajon__cab">
                            <span className="rv-cajon__ceja">{`${String(v.campana.elementos.length)} avisos`}</span>
                            <button
                                type="button"
                                className="rv-enlace-blanco"
                                onClick={() => {
                                    v.campana.leerTodos();
                                    setCajon(null);
                                }}
                            >
                                Marcar como leídos
                            </button>
                            <button type="button" className="rv-bi rv-cajon__x" aria-label="Cerrar los avisos" onClick={() => { setCajon(null); }}>
                                <Icono nombre="x" />
                            </button>
                        </div>
                        <div className="rv-cajon__lista rv-noscroll">
                            {v.campana.elementos.length === 0 && <p className="rv-campana-vacia">{v.campana.vacio}</p>}
                            {v.campana.elementos.map((e, i) => (
                                <TarjetaAviso
                                    key={e.clave}
                                    e={e}
                                    style={{ ...entra(cajon === 'campana' && ultimo === 'campana', i), flexShrink: 0 }}
                                    onClick={() => {
                                        v.campana.tocar(e);
                                    }}
                                    onAccion={() => {
                                        setCajon(null);
                                        v.campana.accion(e);
                                    }}
                                />
                            ))}
                        </div>
                        {v.campana.verTodos !== null && (
                            <button type="button" className="rv-ver-todos" onClick={v.campana.verTodos}>
                                <Icono nombre="bell-ring" />
                                <span>Ver todos los avisos</span>
                            </button>
                        )}
                    </div>
                    <div role="dialog" aria-label="Tus negocios" className="rv-cajon__parte" {...parte('negocios', 'translateX(-24px)')}>
                        <div className="rv-cajon__cab">
                            <span className="rv-cajon__ceja">Tus negocios</span>
                            <button type="button" className="rv-bi rv-cajon__x" aria-label="Cerrar la lista de negocios" onClick={() => { setCajon(null); }}>
                                <Icono nombre="x" />
                            </button>
                        </div>
                        <div className="rv-cajon__lista rv-noscroll">
                            {v.membresias.map((m, i) => {
                                const es = m.business_id === v.negocioId;
                                return (
                                    <button
                                        key={m.business_id}
                                        type="button"
                                        className="rv-fila-negocio"
                                        aria-current={es}
                                        style={entra(cajon === 'negocios', i)}
                                        onClick={() => {
                                            setCajon(null);
                                            v.cambiarNegocio(m.business_id);
                                        }}
                                    >
                                        <Avatar texto={inicialesDe(m)} tamano={38} letra={13} fondo={es ? 'var(--rv-red)' : '#fff'} tinta={es ? '#fff' : 'var(--rv-ink)'} />
                                        <span className="rv-fila-negocio__textos">
                                            <span className="rv-fila-negocio__nombre">{m.business_name}</span>
                                            <span className="rv-fila-negocio__linea">{lineaDe(m)}</span>
                                        </span>
                                        <EtiquetaEstado estado={m.subscription_status} />
                                        {es && <Icono nombre="check" />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div role="dialog" aria-label="Tu cuenta" className="rv-cajon__parte" style={{ ...parte('cuenta', 'translateY(24px)').style, justifyContent: 'flex-end', paddingBottom: 14 }} aria-hidden={cajon !== 'cuenta'}>
                        <div className="rv-cajon__cab">
                            <span className="rv-cajon__ceja">Tu cuenta</span>
                            <button type="button" className="rv-bi rv-cajon__x" aria-label="Cerrar tu cuenta" onClick={() => { setCajon(null); }}>
                                <Icono nombre="x" />
                            </button>
                        </div>
                        <div className="rv-cuenta-tarjeta">
                            <span className="rv-cuenta-tarjeta__nombre">{v.persona.nombre}</span>
                            <span className="rv-cuenta-tarjeta__email">{v.persona.email}</span>
                            <span className="rv-cuenta-tarjeta__rol">{v.persona.rol}</span>
                        </div>
                        <div className="rv-cuenta-lista">
                            {v.menu.perfil.map((e) => (
                                <button
                                    key={e.clave}
                                    type="button"
                                    className="rv-cuenta-op"
                                    onClick={() => {
                                        setCajon(null);
                                        v.ir(e);
                                    }}
                                >
                                    <Icono nombre={dibujoDe(e.icono)} />
                                    <span style={{ flex: 1 }}>{e.etiqueta}</span>
                                </button>
                            ))}
                            {v.personalizar !== null && (
                                <button
                                    type="button"
                                    className="rv-cuenta-op"
                                    onClick={() => {
                                        abrir('personalizar');
                                    }}
                                >
                                    <Icono nombre="sliders-horizontal" />
                                    <span style={{ flex: 1 }}>Personalizar menú</span>
                                </button>
                            )}
                            <button type="button" className="rv-cuenta-op rv-cuenta-op--salir" onClick={v.cerrarSesion}>
                                <Icono nombre="log-out" />
                                <span style={{ flex: 1 }}>Cerrar sesión</span>
                            </button>
                        </div>
                    </div>
                    <div role="dialog" aria-label="Más" className="rv-cajon__parte" {...parte('mas', 'translateX(-24px)')}>
                        <div className="rv-cajon__cab">
                            <span className="rv-cajon__ceja">Más</span>
                            <button type="button" className="rv-bi rv-cajon__x" aria-label="Cerrar «Más»" onClick={() => { setCajon(null); }}>
                                <Icono nombre="x" />
                            </button>
                        </div>
                        <div className="rv-cajon__lista rv-noscroll">
                            {quitadas.map((e, i) => (
                                <button
                                    key={e.clave}
                                    type="button"
                                    className="rv-mas-cajon__fila"
                                    aria-current={e.clave === actualClave ? 'page' : undefined}
                                    style={entra(cajon === 'mas', i)}
                                    onClick={() => {
                                        setCajon(null);
                                        v.ir(e);
                                    }}
                                >
                                    <span className="rv-mas-cajon__baldosa">
                                        <Icono nombre={dibujoDe(e.icono)} />
                                    </span>
                                    <span className="rv-mas-cajon__texto">{e.etiqueta}</span>
                                    <Icono nombre="chevron-right" />
                                </button>
                            ))}
                        </div>
                        {v.personalizar !== null && (
                            <button
                                type="button"
                                className="rv-mas-cajon__personalizar"
                                onClick={() => {
                                    abrir('personalizar');
                                }}
                            >
                                <Icono nombre="sliders-horizontal" />
                                <span>Personalizar menú</span>
                            </button>
                        )}
                    </div>
                    {v.personalizar !== null && (
                        <div role="dialog" aria-label="Personalizar menú" className="rv-cajon__parte" {...parte('personalizar', 'translateX(-24px)')}>
                            <div className="rv-cajon__cab">
                                <span className="rv-cajon__ceja">Personalizar menú</span>
                                <button type="button" className="rv-bi rv-cajon__x" aria-label="Cerrar personalizar menú" onClick={() => { setCajon(null); }}>
                                    <Icono nombre="x" />
                                </button>
                            </div>
                            <PersonalizarMenu
                                menu={v.menu}
                                p={v.personalizar}
                                abierto={cajon === 'personalizar'}
                                onListo={() => {
                                    setCajon(null);
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>

            <aside className="rv-lateral">
                <div className="rv-lateral__logo">
                    <img src={logo} alt="Reservaria" />
                    {v.portal === undefined && (
                        <button
                            id="rv-campana"
                            type="button"
                            className="rv-bi rv-campana-b"
                            aria-label={v.campana.sinLeer > 0 ? `Avisos: ${String(v.campana.sinLeer)} sin leer` : 'Avisos'}
                            aria-expanded={cajon === 'campana'}
                            style={{ border: `1px solid ${cajon === 'campana' ? '#fff' : 'rgba(255,255,255,0.16)'}`, background: cajon === 'campana' ? '#fff' : 'rgba(255,255,255,0.08)', color: cajon === 'campana' ? 'var(--rv-ink)' : '#fff' }}
                            onClick={() => {
                                abrir('campana');
                            }}
                        >
                            <Icono nombre="bell" />
                            {v.campana.sinLeer > 0 && (
                                <span className="rv-campana-b__n" style={{ border: '2px solid var(--rv-black)', opacity: cajon === 'campana' ? 0 : 1, transform: cajon === 'campana' ? 'scale(0.6)' : 'scale(1)' }}>
                                    {v.campana.sinLeer}
                                </span>
                            )}
                        </button>
                    )}
                </div>

                <button
                    type="button"
                    className={`rv-tarjeta-negro${cajon === 'negocios' ? ' rv-tarjeta-negro--abierta' : ''}`}
                    aria-expanded={variosNegocios ? cajon === 'negocios' : undefined}
                    aria-disabled={!variosNegocios}
                    aria-label={variosNegocios ? `Cambiar de negocio. Ahora: ${v.nombreNegocio}` : v.nombreNegocio}
                    onClick={() => {
                        if (variosNegocios) {
                            abrir('negocios');
                        }
                    }}
                >
                    <Avatar texto={v.inicialesNegocio} tamano={36} fondo="var(--rv-red)" tinta="#fff" />
                    <span className="rv-tarjeta-negro__textos">
                        <span className="rv-tarjeta-negro__nombre">{v.nombreNegocio}</span>
                        <span className="rv-tarjeta-negro__linea">{v.lineaNegocio}</span>
                    </span>
                    {variosNegocios && <Icono nombre="chevrons-up-down" />}
                </button>

                <nav ref={nav} aria-label={v.portal === undefined ? 'Menú del panel' : 'Menú del portal'} className="rv-menu rv-noscroll">
                    <div className="rv-menu__lista" style={{ gap: mj.G }}>
                        <span className="rv-menu__pildora" style={{ height: mj.R, transform: `translateY(${String(pildora ?? 0)}px)`, opacity: pildora === null || enPerfil || enMas ? 0 : 1 }} />
                        {grupos.map((g) => {
                            const plegable = grupoPlegable(v.menu, g.clave);
                            const ab = abierto(g.clave);
                            const dentro = !ab && g.entradas.some((e) => e.clave === actualClave);
                            const pendientes = g.entradas.reduce((t, e) => t + (e.contador ?? 0), 0);
                            const idLista = `rv-grupo-${g.clave}`;
                            return (
                                <div key={g.clave} className="rv-menu__grupo">
                                    {plegable ? (
                                        <button
                                            type="button"
                                            className="rv-menu__ceja rv-menu__ceja--boton"
                                            aria-expanded={ab}
                                            aria-controls={idLista}
                                            style={{ height: !ab && fino ? Math.max(mj.H, 30) : mj.H, paddingBottom: mj.hPb, color: dentro ? '#fff' : undefined }}
                                            onClick={() => {
                                                plegar(g.clave);
                                            }}
                                        >
                                            <span>{g.titulo}</span>
                                            <span className="rv-menu__punto" aria-hidden="true" style={{ opacity: dentro ? 1 : 0 }} />
                                            <span style={{ flex: 1 }} />
                                            {!ab && pendientes > 0 && <span className="rv-cifra" style={{ minWidth: 18, height: 18, padding: '0 5px', fontSize: 11, marginBottom: -1 }}>{pendientes}</span>}
                                            <Icono nombre="chevron-down" style={{ transform: ab ? 'none' : 'rotate(-90deg)' }} />
                                        </button>
                                    ) : (
                                        <div className="rv-menu__ceja" style={{ height: mj.H, paddingBottom: mj.hPb }}>
                                            {/* En un portal, la ceja avisa de que es otra zona (§17): «Portal de partners» con su icono. */}
                                            {v.portal !== undefined && <Icono nombre={v.portal.icono} style={{ fontSize: 12 }} />}
                                            {g.titulo}
                                        </div>
                                    )}
                                    <div
                                        id={idLista}
                                        className="rv-menu__entradas"
                                        style={{ height: ab ? g.entradas.length * mj.R : 0, visibility: ab ? 'visible' : 'hidden', ['--rv-vis-retardo' as string]: ab ? '0s' : '360ms' }}
                                    >
                                        {g.entradas.map((e) => {
                                            const activa = e.clave === actualClave && !e.noContratada;
                                            return (
                                                <button
                                                    key={e.clave}
                                                    type="button"
                                                    className="rv-menu__op"
                                                    aria-current={activa ? 'page' : undefined}
                                                    style={{ height: mj.R }}
                                                    onClick={() => {
                                                        v.ir(e);
                                                    }}
                                                >
                                                    <Icono nombre={dibujoDe(e.icono)} />
                                                    <span>{e.etiqueta}</span>
                                                    {e.noContratada && <i className="icon-plus rv-menu__mas" aria-label="No contratado" />}
                                                    {e.contador !== null && e.contador > 0 && <span className="rv-cifra">{e.contador}</span>}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                        {conMas && (
                            <button
                                type="button"
                                className="rv-menu__mas-fila"
                                aria-haspopup="dialog"
                                aria-expanded={masAbierto}
                                aria-current={enMas ? 'page' : undefined}
                                style={{ height: mj.R, background: enMas ? 'var(--rv-red)' : masAbierto ? 'var(--rv-graphite)' : undefined, color: enMas || masAbierto ? '#fff' : undefined }}
                                onClick={() => {
                                    abrir(masAbierto ? cajon : 'mas');
                                }}
                            >
                                <Icono nombre="ellipsis" />
                                <span>Más</span>
                                <span className="rv-menu__mas-n" aria-label={quitadas.length === 1 ? '1 sección' : `${String(quitadas.length)} secciones`}>
                                    {quitadas.length}
                                </span>
                                <Icono nombre="chevron-right" style={{ transform: masAbierto ? 'rotate(180deg)' : 'none' }} />
                            </button>
                        )}
                    </div>
                </nav>

                <div className="rv-lateral__pie">
                    <button
                        type="button"
                        className={`rv-tarjeta-negro${cajon === 'cuenta' ? ' rv-tarjeta-negro--abierta' : ''}`}
                        aria-expanded={cajon === 'cuenta'}
                        aria-label="Tu cuenta"
                        onClick={() => {
                            abrir('cuenta');
                        }}
                    >
                        <Avatar texto={v.persona.iniciales} tamano={36} fondo="#fff" tinta="var(--rv-ink)" />
                        <span className="rv-tarjeta-negro__textos">
                            <span className="rv-tarjeta-negro__nombre">{v.persona.nombre}</span>
                            <span className="rv-tarjeta-negro__linea">{v.persona.rol}</span>
                        </span>
                        <Icono nombre="chevron-up" style={{ transform: cajon === 'cuenta' ? 'rotate(180deg)' : 'none' }} />
                    </button>
                </div>
            </aside>

            <div className="rv-velo" aria-hidden="true" style={{ opacity: cnAbierto ? 1 : 0, pointerEvents: cnAbierto ? 'auto' : 'none' }} onClick={() => { setCajon(null); }} />

            <main className="rv-principal">
                <header className="rv-cabecera">
                    <div className="rv-cabecera__titulo" style={{ marginRight: sW + CURVA.aire + 16 }}>
                        <h1>{v.titulo}</h1>
                        {v.subtitulo !== null && <p className="rv-cabecera__sub">{v.subtitulo}</p>}
                    </div>
                    <div ref={herramientas} className="rv-cabecera__herr">
                        <Ayuda tarjetas={v.herramientas.ayuda} />
                        {v.soloLectura && (
                            <button
                                type="button"
                                className="rv-chip-sl rv-chip-sl--banda"
                                onClick={() => {
                                    abrir('campana');
                                }}
                            >
                                <Icono nombre="lock" />
                                <span>Solo lectura</span>
                            </button>
                        )}
                        {v.herramientas.contexto?.map((c) => <Contexto key={c.clave} etiqueta={c.etiqueta} valor={c.valor} opciones={c.opciones} icono={c.icono} onChange={c.onChange} />)}
                        {v.herramientas.acciones.map((a) => (
                            <Boton key={a.clave} tipo={a.secundaria === true ? 'secundario' : 'principal'} icono={a.icono} disabled={a.desactivado} onClick={a.onClick}>
                                {a.texto}
                            </Boton>
                        ))}
                    </div>
                </header>
                {v.franja !== null && (
                    <div className="rv-avisos-fijos">
                        <Franja
                            key={v.franja.tipo}
                            tipo={v.franja.tipo}
                            titulo={v.franja.titulo}
                            tituloCorto={v.franja.tituloCorto}
                            texto={v.franja.texto}
                            accion={v.franja.accion}
                            cerrable={v.franja.cerrable}
                            etiquetaCerrar={`Cerrar el aviso «${v.franja.tituloCorto}». Seguirá en la campana`}
                            espacio={16}
                            onAccion={() => {
                                if (v.franja !== null) {
                                    v.accionFranja(v.franja);
                                }
                            }}
                            onCerrar={() => {
                                if (v.franja !== null) {
                                    v.cerrarFranja(v.franja);
                                }
                            }}
                        />
                    </div>
                )}
                <div id="rv-contenido" className="rv-contenido">
                    <div className="rv-contenido__columna">
                        {v.contenido}
                        <div style={{ height: 64, flexShrink: 0 }} />
                    </div>
                </div>
            </main>
            <Privacidad abajo="16px" />
        </div>
    );
}
